import { useEffect, useState } from 'react'

export const SCRIPT_STATES = {
  LOADING: 'LOADING',
  IDLE: 'IDLE',
  ERROR: 'ERROR',
  READY: 'READY',
} as const

export type ScriptStateType = (typeof SCRIPT_STATES)[keyof typeof SCRIPT_STATES]

/** Loads an external script once */
const useScript = (src: string) => {
  const [status, setStatus] = useState<ScriptStateType>(src ? SCRIPT_STATES.LOADING : SCRIPT_STATES.IDLE)

  useEffect(() => {
    if (!src) {
      setStatus(SCRIPT_STATES.IDLE)
      return
    }

    let script: HTMLScriptElement | null = document.querySelector(`script[src="${src}"]`)

    if (!script) {
      script = document.createElement('script')
      script.src = src
      script.async = true
      script.setAttribute('data-status', SCRIPT_STATES.LOADING)
      document.body.appendChild(script)

      const setDataStatus = (event: Event) => {
        if (script) {
          script.setAttribute('data-status', event.type === 'load' ? SCRIPT_STATES.READY : SCRIPT_STATES.ERROR)
        }
      }
      script.addEventListener('load', setDataStatus)
      script.addEventListener('error', setDataStatus)
    } else {
      // eslint-disable-next-line i18next/no-literal-string -- HTML data attribute name
      setStatus((script.getAttribute('data-status') as ScriptStateType) || SCRIPT_STATES.LOADING)
    }

    const setStateStatus = (event: Event) => {
      setStatus(event.type === 'load' ? SCRIPT_STATES.READY : SCRIPT_STATES.ERROR)
    }

    script.addEventListener('load', setStateStatus)
    script.addEventListener('error', setStateStatus)

    return () => {
      if (script) {
        script.removeEventListener('load', setStateStatus)
        script.removeEventListener('error', setStateStatus)
      }
    }
  }, [src])

  return status
}

export default useScript
